import { Component, ContentChild, EventEmitter, forwardRef, Input, Output, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-select-button',
    templateUrl: './select-button.component.html',
    styleUrls: ['./select-button.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectButtonComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class SelectButtonComponent implements ControlValueAccessor {
    @Input() options: any[] = [];
    /** PrimeNG default is "label" */
    @Input() optionLabel = 'label';
    /** PrimeNG: if not provided, value is the option object itself */
    @Input() optionValue?: string;
    /** PrimeNG: name of boolean field that disables an option */
    @Input() optionDisabled?: string;
    /** PrimeNG: multiple selection mode */
    @Input() multiple = false;

    @Input() disabled = false;

    /** Maintain API parity with `<p-selectButton (onChange)=...>` */
    @Output() onChange = new EventEmitter<any>();

    /** Support existing Prime template style used in repo: `<ng-template #item let-item>...</ng-template>` */
    @ContentChild('item', { read: TemplateRef }) itemTemplate?: TemplateRef<any>;

    value: any = this.multiple ? [] : undefined;

    private onChangeFn: (value: any) => void = () => {};
    private onTouchedFn: () => void = () => {};

    writeValue(value: any): void {
        this.value = value;
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    getOptionLabel(option: any): string {
        if (option === undefined || option === null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return String(option);
        }
        return String(option?.[this.optionLabel] ?? '');
    }

    getOptionValue(option: any): any {
        if (option === undefined || option === null) {
            return undefined;
        }
        if (!this.optionValue) {
            return option;
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[this.optionValue];
    }

    isOptionDisabled(option: any): boolean {
        if (this.disabled) {
            return true;
        }
        if (!this.optionDisabled) {
            return false;
        }
        if (!option || typeof option !== 'object') {
            return false;
        }
        return Boolean(option?.[this.optionDisabled]);
    }

    isSelected(option: any): boolean {
        const optValue = this.getOptionValue(option);
        if (this.multiple) {
            const arr = Array.isArray(this.value) ? this.value : [];
            return arr.some((v) => v === optValue);
        }
        return this.value === optValue;
    }

    onOptionClick(evt: MouseEvent, option: any): void {
        if (this.isOptionDisabled(option)) {
            evt.preventDefault();
            evt.stopPropagation();
            return;
        }

        const optValue = this.getOptionValue(option);
        let next: any;
        if (this.multiple) {
            const arr = Array.isArray(this.value) ? [...this.value] : [];
            const idx = arr.findIndex((v) => v === optValue);
            if (idx >= 0) {
                arr.splice(idx, 1);
            } else {
                arr.push(optValue);
            }
            next = arr;
        } else {
            next = optValue;
        }

        this.value = next;
        this.onChangeFn(next);
        this.onTouchedFn();
        this.onChange.emit({ originalEvent: evt, value: next });
    }
}

